import React, { useState, useRef, useEffect } from "react";
import thresholdsData from "./range-risk-thresholds.json"; // Import thresholds
import pennyThresholdsData from "./penny-range-risk-thresholds.json";
import defaultData from "./_49_default_symbol.json"; // Import default data
import grey from "../node_modules/@mui/material/colors/grey";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

//moving risk calculation functions to separate files - minimize clutter
import { formatIntegerWithCommas, formatLastPrice, formatRiskAbbreviated, formatVolume, parseCurrencyInput, parseSymbols } from './49/49_utils';
import { analyticsHandler } from "./eventLogger.js"; // Add this import

const MODULE_NAME = "_49_range_based_risk";
const DEBUG_PREFIX = "[DEBUG 49_range_based_risk]";
const Printdebug = true; // Set to false to disable debug logging

const SHOW_UNDERLINES = true; // Set to false to hide colored underlines

const LIQUIDITY_VOLUME_THRESHOLD = 12000000; // 12M
const PERIOD_OPTIONS = [7, 30, 60, 90];

// === RISK WEIGHTS CONFIGURATION ===numbers should add up to 1.0
const RISK_WEIGHTS = {
    last_price: 0.15,
    beta: 0.20,
    average_range_percentage: 0.20,
    dollar_percentage_of_account_balance: 0.15,
    est_risk_dollar_percentage: 0.15,
    avg_daily_volume: 0.15
};

const RISK_WEIGHTS_PENNY = {
    last_price: 0.2,
    beta: 0.01,
    average_range_percentage: 0.35,
    dollar_percentage_of_account_balance: 0.10,
    est_risk_dollar_percentage: 0.25,
    avg_daily_volume: 0.04
};

// --- Gear Icon Component ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// === RISK LEVEL SCORES ===

// src/49/49_riskCalculations.js

const RISK_LEVEL_SCORES = {
    low: 1,
    medium: 2,
    high: 3
};

function getOverallRiskColor(totalScore) {
    if (totalScore < 1.5) return { color: "#9e9e9e", label: "Neutral" };       // Gray
    if (totalScore < 2.0) return { color: "#fbc02d", label: "Mild" };          // Yellow
    if (totalScore < 2.5) return { color: "#ff9800", label: "Elevated" };      // Orange
    return { color: "#e53935", label: "High" };                                 // Red
}
function getRiskLevel(field, value, lastPrice = null, accountBalance = null, isPenny = false) {
    // Use penny thresholds if isPenny and field exists in penny thresholds
    const thresholds = isPenny && pennyThresholdsData.thresholds[field]
        ? pennyThresholdsData.thresholds[field]
        : thresholdsData.thresholds[field];
    if (!thresholds) return { level: "low", explanation: "No threshold defined" };

    let level = "low";
    let explanation = "";

    if (field === "last_price") {
        // Lower price = higher risk
        if (value <= thresholds.high) {
            level = "high";
            explanation = `Price $${value} ≤ $${thresholds.high}`;
        } else if (value <= thresholds.low) {
            level = "medium";
            explanation = `Price $${value} between $${thresholds.high} and $${thresholds.low}`;
        } else {
            level = "low";
            explanation = `Price $${value} > $${thresholds.low}`;
        }
    }

    if (field === "beta") {
        // Higher beta = higher risk
        if (value >= thresholds.high) {
            level = "high";
            explanation = `Beta ${value.toFixed(2)} ≥ ${thresholds.high}`;
        } else if (value >= thresholds.low) {
            level = "medium";
            explanation = `Beta ${value.toFixed(2)} between ${thresholds.low} and ${thresholds.high}`;
        } else {
            level = "low";
            explanation = `Beta ${value.toFixed(2)} < ${thresholds.low}`;
        }
    }

    if (field === "average_range_percentage" && lastPrice !== null && lastPrice > 0) {
        const percent = (value / lastPrice) * 100;
        if (percent >= thresholds.high) {
            level = "high";
            explanation = `Range ${percent.toFixed(1)}% ≥ ${thresholds.high}%`;
        } else if (percent >= thresholds.low) {
            level = "medium";
            explanation = `Range ${percent.toFixed(1)}% between ${thresholds.low}% and ${thresholds.high}%`;
        } else {
            level = "low";
            explanation = `Range ${percent.toFixed(1)}% < ${thresholds.low}%`;
        }
    }

    if (field === "dollar_percentage_of_account_balance") {
        if (value >= thresholds.high) {
            level = "high";
            explanation = `Position ${value.toFixed(0)}% ≥ ${thresholds.high}%`;
        } else if (value >= thresholds.low) {
            level = "medium";
            explanation = `Position ${value.toFixed(0)}% between ${thresholds.low}% and ${thresholds.high}%`;
        } else {
            level = "low";
            explanation = `Position ${value.toFixed(0)}% < ${thresholds.low}%`;
        }
    }

    if (field === "est_risk_dollar_percentage" && accountBalance !== null && accountBalance > 0) {
        const percent = (value / accountBalance) * 100;
        if (percent >= thresholds.high) {
            level = "high";
            explanation = `Est. risk ${percent.toFixed(1)}% ≥ ${thresholds.high}%`;
        } else if (percent >= thresholds.low) {
            level = "medium";
            explanation = `Est. risk ${percent.toFixed(1)}% between ${thresholds.low}% and ${thresholds.high}%`;
        } else {
            level = "low";
            explanation = `Est. risk ${percent.toFixed(1)}% < ${thresholds.low}%`;
        }
    }

    if (field === "avg_daily_volume") {
        // Lower volume = higher risk
        if (value <= thresholds.high) {
            level = "high";
            explanation = `Volume ${(value / 1e6).toFixed(1)}M ≤ ${thresholds.high / 1e6}M`;
        } else if (value <= thresholds.low) {
            level = "medium";
            explanation = `Volume ${(value / 1e6).toFixed(1)}M between ${thresholds.high / 1e6}M and ${thresholds.low / 1e6}M`;
        } else {
            level = "low";
            explanation = `Volume ${(value / 1e6).toFixed(1)}M > ${thresholds.low / 1e6}M`;
        }
    }

    return { level, explanation };
}

export function calculateRiskScore(metrics, weights = RISK_WEIGHTS, isPenny = false) {
    const breakdown = [];
    let totalWeightedScore = 0;
    let totalWeight = 0;

    const fieldLabels = {
        last_price: "Last Price",
        beta: "Beta",
        average_range_percentage: "Avg Range (%)",
        dollar_percentage_of_account_balance: "Position Size",
        est_risk_dollar_percentage: "Est. Dollar Risk",
        avg_daily_volume: "Daily Volume"
    };

    for (const [field, weight] of Object.entries(weights)) {
        const metricData = metrics[field];
        if (metricData === undefined || metricData === null || metricData === "N/A") {
            continue;
        }

        const { level, explanation } = getRiskLevel(
            field,
            metricData.value,
            metricData.lastPrice,
            metricData.accountBalance,
            isPenny
        );

        const score = RISK_LEVEL_SCORES[level];
        const weightedScore = score * weight;

        breakdown.push({
            field,
            label: fieldLabels[field] || field,
            level,
            weight: (weight * 100).toFixed(0) + "%",
            score,
            weightedScore: weightedScore.toFixed(2),
            explanation
        });

        totalWeightedScore += weightedScore;
        totalWeight += weight;
    }

    // Normalize if not all metrics are present
    const normalizedScore = totalWeight > 0 ? totalWeightedScore / totalWeight : 1;

    return {
        totalScore: normalizedScore,
        breakdown,
        riskColor: getOverallRiskColor(normalizedScore)
    };
}

//Separate JS FILE FOR RISK CALCULATION LOGIC




function getUnderlineColor(field, value, lastPrice = null) {
    const thresholds = thresholdsData.thresholds[field];
    let color = "transparent";
    let explanation = "";

    if (field === "last_price") {
        if (value <= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] last_price (${value}) <= high (${thresholds.high}): RED underline (high risk)`;
        } else if (value > thresholds.high && value <= thresholds.low) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] last_price (${value}) > high (${thresholds.high}) && <= low (${thresholds.low}): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] last_price (${value}) > low (${thresholds.low}): GREEN underline (low risk)`;
        }
        //console.debug(explanation);
    }

    if (field === "average_range_percentage" && lastPrice !== null && lastPrice > 0) {
        const percent = (value / lastPrice) * 100;
        if (percent >= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] average_range_percentage (${percent.toFixed(2)}%) ≥ high (${thresholds.high}%): RED underline (high risk)`;
        } else if (percent >= thresholds.low && percent < thresholds.high) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] average_range_percentage (${percent.toFixed(2)}%) ≥ low (${thresholds.low}%) && < high (${thresholds.high}%): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] average_range_percentage (${percent.toFixed(2)}%) < low (${thresholds.low}%): GREEN underline (low risk)`;
        }
        //console.debug(explanation);
    }

    if (field === "beta") {
        if (value >= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] beta (${value}) ≥ high (${thresholds.high}): RED underline (high risk)`;
        } else if (value >= thresholds.low && value < thresholds.high) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] beta (${value}) ≥ low (${thresholds.low}) && < high (${thresholds.high}): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] beta (${value}) < low (${thresholds.low}): GREEN underline (low risk)`;
        }
        //console.debug(explanation);
    }

    if (field === "avg_daily_volume") {
        if (value <= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] avg_daily_volume (${value}) <= high (${thresholds.high}): RED underline (high risk)`;
        } else if (value > thresholds.high && value <= thresholds.low) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] avg_daily_volume (${value}) > high (${thresholds.high}) && <= low (${thresholds.low}): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] avg_daily_volume (${value}) > low (${thresholds.low}): GREEN underline (low risk)`;
        }
        console.debug(explanation);
    }

    if (field === "est_risk_dollar_percentage" && lastPrice !== null && lastPrice > 0) {
        const percent = (value / lastPrice) * 100;
        if (percent >= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] est_risk_dollar_percentage (${percent.toFixed(2)}%) >= high (${thresholds.high}%): RED underline (high risk)`;
        } else if (percent >= thresholds.low && percent < thresholds.high) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] est_risk_dollar_percentage (${percent.toFixed(2)}%) >= low (${thresholds.low}%) && < high (${thresholds.high}%): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] est_risk_dollar_percentage (${percent.toFixed(2)}%) < low (${thresholds.low}%): GREEN underline (low risk)`;
        }
        console.debug(explanation);
    }

    if (field === "dollar_percentage_of_account_balance") {
        if (value >= thresholds.high) {
            color = "#e53935"; // Red
            explanation = `[DEBUG] dollar_percentage_of_account_balance (${value}%) >= high (${thresholds.high}%): RED underline (high risk)`;
        } else if (value >= thresholds.low && value < thresholds.high) {
            color = "#fbc02d"; // Yellow
            explanation = `[DEBUG] dollar_percentage_of_account_balance (${value}%) >= low (${thresholds.low}%) && < high (${thresholds.high}%): YELLOW underline (medium risk)`;
        } else {
            color = "#43a047"; // Green
            explanation = `[DEBUG] dollar_percentage_of_account_balance (${value}%) < low (${thresholds.low}%): GREEN underline (low risk)`;
        }
        console.debug(explanation);
    }

    return color;
}


//ends //Separate JS FILE FOR RISK CALCULATION LOGIC


// === CASH BREAKDOWN MODAL COMPONENT ===
function CashBreakdownModal({ open, onClose, symbol, cashPercentages, avgHigh, avgRange, accountVal, getUnderlineColor, formatIntegerWithCommas, formatRiskAbbreviated }) {
    if (!open) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
            onClick={onClose}
            data-analytics={MODULE_NAME}

        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 10,
                    minWidth: 420,
                    maxWidth: 540,
                    padding: 24,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    position: "relative"
                }}
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: 12,
                        right: 16,
                        background: "none",
                        border: "none",
                        fontSize: 22,
                        color: "#888",
                        cursor: "pointer"
                    }}
                    aria-label="Close"
                >×</button>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18, color: "#222" }}>
                    {symbol} Cash Breakdown
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: "#e3e8ef" }}>
                            <th style={{ padding: "8px 12px", fontWeight: 700, color: "#444", textAlign: "left" }}>Symbol</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th style={{ padding: "8px 12px", fontWeight: 700, color: "#444", textAlign: "center" }}>Qty</th>
                            <th style={{ padding: "8px 12px", fontWeight: 700, color: "#444", textAlign: "center" }}>% of Cash</th>
                            <th style={{ padding: "8px 12px", fontWeight: 700, color: "#444", textAlign: "center" }}>Est. Dollar Risk</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {cashPercentages.map(({ pct, label }) => {
                            const cashAmount = accountVal * pct;
                            const qty = avgHigh ? Math.floor(cashAmount / avgHigh) : null;
                            const estRisk = (avgRange && avgHigh) ? avgRange * qty : null;
                            const pctValue = pct * 100;
                            return (
                                <tr key={symbol + "_percash_" + label} style={{ background: "#f5f7fa" }}>
                                    <td style={{ padding: "8px 12px", textAlign: "left", fontWeight: 500, color: "#1976d2" }}>
                                        <span style={{ marginLeft: 24 }}>{symbol}</span>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                        {qty !== null ? formatIntegerWithCommas(qty) : "N/A"}
                                    </td>
                                    <td style={{ padding: "8px 12px", textAlign: "center", position: "relative" }}>
                                        <div>
                                            {pctValue.toFixed(0) + "%"}
                                        </div>
                                        <div
                                            style={{
                                                borderBottom: `4px solid ${getUnderlineColor("dollar_percentage_of_account_balance", pctValue)}`,
                                                width: "60%",
                                                margin: "0 auto"
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                        <div>
                                            {estRisk !== null && !isNaN(estRisk) ? formatRiskAbbreviated(estRisk) : "N/A"}
                                        </div>
                                        {estRisk !== null && !isNaN(estRisk) && (
                                            <div
                                                style={{
                                                    borderBottom: `4px solid ${getUnderlineColor("est_risk_dollar_percentage", estRisk, accountVal)}`,
                                                    width: "60%",
                                                    margin: "0 auto"
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// === RISK WEIGHT CONFIG COMPONENT ===
function RiskWeightConfig({ weights, onWeightsChange, isOpen, onClose }) {
    if (!isOpen) return null;

    const handleWeightChange = (field, newWeight) => {
        const weight = Math.max(0, Math.min(1, isNaN(newWeight) ? 0 : newWeight)); // Clamp between 0 and 1
        onWeightsChange({
            ...weights,
            [field]: weight
        });
    };

    const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0).toFixed(2);
    const isValid = parseFloat(totalWeight) === 1.00;

    return (
        <>
            <div 
                style={{ 
                    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", 
                    background: "rgba(24,26,37,0.35)", backdropFilter: "blur(2px)", zIndex: 9998 
                }} 
                onClick={onClose} 
            />
            <div 
                style={{ 
                    position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", 
                    zIndex: 9999, minWidth: 500, background: "#181A25", borderRadius: 12, 
                    boxShadow: "0 8px 32px rgba(0,0,0,0.5)", padding: 24, border: "1px solid #333" 
                }}
            >
                <button 
                    onClick={onClose} 
                    style={{ position: "absolute", top: 12, right: 18, background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}
                >✕</button>
                
                <h3 style={{ marginTop: 0, marginBottom: 8, color: "#fff" }}>Risk Weight Configuration</h3>
                <p style={{ fontSize: 13, color: "#aaa", marginBottom: 20 }}>
                    Adjust weights for stocks priced greater than $5.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
                    {Object.entries(weights).map(([field, weight]) => {
                        const label = {
                            last_price: "Last Price",
                            beta: "Beta",
                            average_range_percentage: "Avg Range (%)",
                            dollar_percentage_of_account_balance: "Position Size",
                            est_risk_dollar_percentage: "Est. Dollar Risk",
                            avg_daily_volume: "Daily Volume"
                        }[field];

                        return (
                            <div key={field} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <span style={{ color: "#eee", fontSize: 14 }}>{label}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={weight}
                                        onChange={e => handleWeightChange(field, parseFloat(e.target.value))}
                                        style={{ width: 120, accentColor: "#1976d2" }}
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={weight}
                                        onChange={e => handleWeightChange(field, parseFloat(e.target.value))}
                                        style={{ 
                                            width: 60, textAlign: "center", background: "#131626", 
                                            border: "1px solid #444", color: "#fff", borderRadius: 4, padding: "2px 0" 
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ 
                    borderTop: "1px solid #333", paddingTop: 16, display: "flex", 
                    justifyContent: "space-between", alignItems: "center" 
                }}>
                    <div style={{ fontSize: 14, color: isValid ? "#43a047" : "#e53935", fontWeight: 600 }}>
                        Total Weight: {totalWeight} {isValid ? "✓" : "(!)"}
                    </div>
                    <button 
                        className="primarybtn" 
                        style={{ padding: "8px 24px" }}
                        onClick={onClose}
                    >
                        Save & Close
                    </button>
                </div>
            </div>
        </>
    );
}

// === RISK POPOVER COMPONENT (now a centered modal) ===
function RiskPopover({ isOpen, onClose, breakdown, riskColor, anchorRef, symbol }) {
    if (!isOpen) return null;

    const levelColors = {
        low: "#43a047",
        medium: "#fbc02d",
        high: "#e53935"
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-live="polite"
                style={{
                    background: "#fff",
                    borderRadius: 10,
                    minWidth: 400,
                    maxWidth: 500,
                    padding: 24,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    position: "relative"
                }}
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: 12,
                        right: 16,
                        background: "none",
                        border: "none",
                        fontSize: 22,
                        color: "#888",
                        cursor: "pointer"
                    }}
                    aria-label="Close"
                >×</button>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <span style={{ fontWeight: 700, fontSize: 18, color: "#222" }}>Risk Breakdown {symbol ? ` – ${symbol}` : ""}</span>
                    <span
                        style={{
                            padding: "4px 12px",
                            borderRadius: 4,
                            background: riskColor.color,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600
                        }}
                    >
                        {riskColor.label}
                    </span>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: "#444" }}>
                            <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 13, color: "#f5f5f5", fontWeight: 700 }}>Category</th>
                            <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 13, color: "#f5f5f5", fontWeight: 700 }}>Level</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", fontSize: 13, color: "#f5f5f5", fontWeight: 700 }}>Weight</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", fontSize: 13, color: "#f5f5f5", fontWeight: 700 }}>Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {breakdown.map((item) => (
                            <tr key={item.field} style={{ borderBottom: "1px solid #eee" }}>
                                <td style={{ padding: "10px 12px", fontSize: 13 }}>
                                    <div style={{ color: "#444", fontWeight: 600 }}>
                                        {item.label}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{item.explanation}</div>
                                </td>
                                <td style={{ textAlign: "center", padding: "10px 12px" }}>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            padding: "3px 8px",
                                            borderRadius: 4,
                                            background: levelColors[item.level],
                                            color: "#fff",
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {item.level}
                                    </span>
                                </td>
                                <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, color: "#666" }}>
                                    {item.weight}
                                </td>
                                <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#666" }}>
                                    {item.weightedScore}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #eee", fontSize: 11, color: "#888", textAlign: "center" }}>
                    Click outside or press × to close
                </div>
            </div>
        </div>
    );
}

// === SYMBOL CELL COMPONENT ===
function SymbolCell({ symbol, metrics, isPenny, screen }) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const anchorRef = useRef(null);

    // Use RISK_WEIGHTS_PENNY for penny stocks, otherwise RISK_WEIGHTS
    const weights = isPenny ? RISK_WEIGHTS_PENNY : RISK_WEIGHTS;
    const { totalScore, breakdown, riskColor } = calculateRiskScore(metrics, weights, isPenny);

    const handleToggle = (e) => {
        const newState = !isPopoverOpen;
        setIsPopoverOpen(newState);
        // Log analytics event when opening the modal
        if (newState) {
            analyticsHandler({
                event_type: "button_click",
                module_name: MODULE_NAME,
                subcomponent: "symbol-risk-modal",
                screen,
                metadata: { symbol, isPenny }
            })(e);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle(e);
        }
        if (e.key === "Escape") {
            setIsPopoverOpen(false);
        }
    };

    return (
        <td style={{ padding: "10px 24px", textAlign: "center", position: "relative" }}>
            <span
                ref={anchorRef}
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={isPopoverOpen}
                aria-describedby={isPopoverOpen ? `risk-popover-${symbol}` : undefined}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                style={{
                    cursor: "pointer",
                    fontWeight: 600,
                    display: "inline-block",
                    position: "relative",
                    paddingBottom: 4
                }}
            >
                {symbol}{isPenny && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
                {SHOW_UNDERLINES && (
                    <span
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: riskColor.color,
                            borderRadius: 2
                        }}
                    />
                )}
            </span>

            <RiskPopover
                isOpen={isPopoverOpen}
                onClose={() => setIsPopoverOpen(false)}
                breakdown={breakdown}
                riskColor={riskColor}
                anchorRef={anchorRef}
                symbol={symbol}
            />
        </td>
    );
}



function RangeBasedRisk({ screen = window.location.pathname }) {
    const [data, setData] = useState([]);
    const [tooltip, setTooltip] = useState("");
    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
    const [error, setError] = useState(null);
    const [accountBalance, setAccountBalance] = useState("50000");
    const [symbolInput, setSymbolInput] = useState("GOOGL,APLD");
    const [sliderValues, setSliderValues] = useState({});
    const [showGlossary, setShowGlossary] = useState(false);
    const [expandedQty, setExpandedQty] = useState({});
    const [modalSymbol, setModalSymbol] = useState(null);
    const [cashPercent, setCashPercent] = useState(0.1); 
    const [weights, setWeights] = useState(RISK_WEIGHTS);
    const [showWeightsConfig, setShowWeightsConfig] = useState(false);
    
    // Combined initialization effect
    useEffect(() => {
        if (Printdebug) {
            console.log(`${DEBUG_PREFIX} ========== INITIALIZATION STARTED ==========`);
        }

        if (!defaultData || !defaultData.results) {
            if (Printdebug) console.error(`${DEBUG_PREFIX} defaultData is missing results`);
            return;
        }

        const initialResults = defaultData.results;
        const newSliderValues = {};
        const accountVal = Number(parseCurrencyInput(accountBalance));

        const processedData = initialResults.map((row) => {
            const periodData = row.periods && row.periods[period] ? row.periods[period] : {};
            
            // Set initial slider values from default data
            if (periodData.average_range !== undefined && periodData.average_range !== "N/A") {
                newSliderValues[row.symbol] = periodData.average_range;
            }

            const avgRange = periodData.average_range !== undefined && periodData.average_range !== "N/A"
                ? Number(periodData.average_range)
                : 1;
            const avgHigh = periodData.average_high !== undefined && periodData.average_high !== "N/A"
                ? Number(periodData.average_high)
                : 1;
            const lastPrice = row.last_close !== undefined && row.last_close !== null && row.last_close !== "N/A"
                ? Number(row.last_close)
                : null;

            const maxQty = avgHigh ? Math.floor((accountVal * cashPercent) / avgHigh) : null;
            const perOfCashPct = accountVal > 0 ? ((accountVal * cashPercent) / accountVal) * 100 : 0;
            const estRiskLow = (avgRange && avgHigh) ? avgRange * maxQty : null;
            const avgVol = periodData.avg_daily_volume_10d;

            // Build metrics object for risk calculation
            const riskMetrics = {
                last_price: lastPrice !== null ? { value: lastPrice } : null,
                beta: row.beta !== undefined && row.beta !== null && !isNaN(row.beta)
                    ? { value: Number(row.beta) }
                    : null,
                average_range_percentage: avgRange !== undefined && avgRange !== "N/A" && lastPrice !== null
                    ? { value: avgRange, lastPrice }
                    : null,
                dollar_percentage_of_account_balance: { value: perOfCashPct },
                est_risk_dollar_percentage: estRiskLow !== null
                    ? { value: estRiskLow, accountBalance: accountVal }
                    : null,
                avg_daily_volume: avgVol !== undefined && avgVol !== "N/A" && !isNaN(avgVol)
                    ? { value: Number(avgVol) }
                    : null
            };

            const weights = lastPrice < 5 ? RISK_WEIGHTS_PENNY : RISK_WEIGHTS;
            const { totalScore, breakdown, riskColor } = calculateRiskScore(riskMetrics, weights, lastPrice < 5);
            
            return {
                ...row,
                riskMetrics,
                totalRiskScore: totalScore,
                riskColor,
                riskBreakdown: breakdown
            };
        });

        if (Printdebug) {
            console.log(`${DEBUG_PREFIX} Calling setData with ${processedData.length} rows`);
        }

        setData(processedData);
        setSliderValues(newSliderValues);

        if (defaultData.tooltip) {
            setTooltip(defaultData.tooltip);
        }

    }, [period, cashPercent]); // Only recalculate when user changes period or cash selection

    const fetchData = () => {
        setLoading(true);
        setError(null);
        const symbols = parseSymbols(symbolInput);
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/api/range-based-risk-multi?period_days=${period}&account_balance=${parseCurrencyInput(accountBalance)}&symbols=${symbols}`,
                { withCredentials: true } // <-- send cookies/session for flask_login
            )
            .then((res) => {
                const json = res.data;
                if (json.error) {
                    setError(json.error);
                    setData([]);
                } else {
                    setData(json.results || []);
                    setTooltip(json.tooltip || "");
                    setAccountBalance(formatCurrencyInput(json.account_balance || "50000"));
                    const newSliderValues = {};
                    (json.results || []).forEach(row => {
                        const periodData = row.periods && row.periods[period] ? row.periods[period] : {};
                        if (periodData.average_range !== undefined && periodData.average_range !== "N/A") {
                            newSliderValues[row.symbol] = periodData.average_range;
                        }
                    });
                    setSliderValues(newSliderValues);
                }
                setLoading(false);
            })
            .catch((err) => {
                setError("Failed to fetch data");
                setLoading(false);
            });
    };

    function formatCurrencyInput(value) {
        if (!value) return "";
        const num = Number(value.toString().replace(/[^0-9.]/g, ""));
        if (isNaN(num)) return "";
        return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }

    const handleAccountBalanceChange = (e) => {
        setAccountBalance(formatCurrencyInput(e.target.value));
        // Log analytics event
        analyticsHandler({
            event_type: "input_change",
            module_name: MODULE_NAME,
            subcomponent: "account-balance",
            screen
        })(e);
    };

    const handleSymbolInputChange = (e) => {
        setSymbolInput(e.target.value.toUpperCase());
        // Log analytics event

    };

    const handleGetDataClick = (e) => {
        fetchData();
        // Log analytics event
        analyticsHandler({
            event_type: "button_click",
            module_name: MODULE_NAME,
            subcomponent: "get-data-btn",
            screen
        })(e);
    };

    const handlePeriodChange = (e) => {
        setPeriod(Number(e.target.value));
        // Log analytics event
        analyticsHandler({
            event_type: "dropdown_change",
            module_name: MODULE_NAME,
            subcomponent: "period-select",
            screen
        })(e);
    };

    const handleCashPercentClick = (val) => {
        return (e) => {
            setCashPercent(val);
            // Log analytics event
            analyticsHandler({
                event_type: "button_click",
                module_name: MODULE_NAME,
                subcomponent: `cash-percent-${(val * 100).toFixed(0)}`,
                screen
            })(e);
        };
    };

    const handleSliderChange = (symbol, value) => {
        setSliderValues(prev => ({
            ...prev,
            [symbol]: parseFloat(value)
        }));
    };

    const handleToggleQty = (symbol) => {
        setExpandedQty(prev => ({
            ...prev,
            [symbol]: !prev[symbol]
        }));
    };

    return (
        <div className="range-risk-container" data-analytics={MODULE_NAME}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <h2 style={{ margin: 0, display: "flex", alignItems: "center" }}>
                    📊 Volatility-Based Risk Calculator
                    <span
                        style={{
                            marginLeft: 8,
                            cursor: "pointer",
                            color: "#888",
                            fontSize: 18,
                            display: "inline-flex",
                            alignItems: "center"
                        }}
                        title={tooltip}
                    >🛈</span>
                    <span 
                        onClick={() => setShowWeightsConfig(true)} 
                        style={{ 
                            marginLeft: 12,
                            cursor: "pointer", 
                            display: "inline-flex", 
                            alignItems: "center"
                        }}
                        title="Risk Weight Configuration"
                    >
                        <GearIcon size={20} color="#888" />
                    </span>
                </h2>
            </div>

            {/* Risk Weight Configuration Modal */}
            <RiskWeightConfig
                isOpen={showWeightsConfig}
                onClose={() => setShowWeightsConfig(false)}
                weights={weights}
                onWeightsChange={(newWeights) => {
                    setWeights(newWeights);
                    // Update data with new weights
                    const updatedData = data.map(row => {
                        const w = row.last_close < 5 ? RISK_WEIGHTS_PENNY : newWeights;
                        const { totalScore, breakdown, riskColor } = calculateRiskScore(row.riskMetrics, w, row.last_close < 5);
                        return {
                            ...row,
                            totalRiskScore: totalScore,
                            riskColor,
                            riskBreakdown: breakdown
                        };
                    });
                    setData(updatedData);
                }}
            />

            {/* Main input row - redesigned */}
            <div
                style={{
                    marginBottom: 16,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    background: "#131626",
                    borderRadius: 10,
                    padding: "18px 18px 8px 18px",
                    boxShadow: "0 1px 4px 0 rgba(60,60,60,0.03)",
                    border: "1px solid #e0e0e0"
                }}
            >
                <label htmlFor="account-balance" style={{ marginRight: 12, fontWeight: 500 }}>
                    Cash:
                </label>
                <input
                    id="account-balance"
                    className="input-text"
                    type="text"
                    value={accountBalance}
                    onChange={handleAccountBalanceChange}
                    style={{ width: 140, marginRight: 24, marginTop: 0 }}
                    inputMode="numeric"
                    autoComplete="off"
                />
                {/* Quick cash links below the textbox, smaller font */}
                <div style={{ width: 140, margin: "2px 0 8px 0" }}>
                    {[
                        { label: "30K", value: 30000 },
                        { label: "60K", value: 60000 },
                        { label: "90K", value: 90000 }
                    ].map((item, idx) => (
                        <a
                            key={item.label}
                            href="#"
                            onClick={e => {
                                e.preventDefault();
                                setAccountBalance(formatCurrencyInput(item.value));
                                analyticsHandler({
                                    event_type: "button_click",
                                    module_name: MODULE_NAME,
                                    subcomponent: `cash-quicklink-${item.value}`,
                                    screen
                                })(e);
                            }}
                            style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                fontWeight: 500,
                                fontSize: 12,
                                marginRight: idx < 2 ? 12 : 0,
                                cursor: "pointer"
                            }}
                        >
                            {item.label}
                        </a>
                    ))}
                </div>
                <label htmlFor="symbol-input" style={{ marginRight: 12, fontWeight: 500 }}>
                    Symbols:
                </label>
                <input
                    id="symbol-input"
                    className="input-text"
                    type="text"
                    value={symbolInput}
                    onChange={handleSymbolInputChange}
                    style={{ width: 180, marginRight: 24 }}
                    placeholder="e.g. GOOGL,APLD"
                    autoComplete="off"
                />
                <button
                    id="get-data-btn"
                    className="primarybtn"
                    style={{
                        padding: "6px 24px",
                        fontWeight: 500,
                        marginTop: 8,
                        marginBottom: 8,
                        minWidth: 110,
                        position: "relative"
                    }}
                    onClick={handleGetDataClick}
                    disabled={loading}
                >
                    Get data
                </button>
                {loading && (
                    <span style={{ display: "inline-block", verticalAlign: "middle", marginLeft: 12 }}>
                        <CircularProgress size={22} thickness={5} />
                    </span>
                )}
            </div>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Lookback period (days):{" "}
                    <select
                        id="period-select"
                        value={period}
                        onChange={handlePeriodChange}
                        style={{ marginLeft: 8, minWidth: 80 }}
                    >
                        {PERIOD_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {/* Quick cash % buttons */}
            <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500, marginRight: 8 }}>Quick cash %:</span>
                {[0.1, 0.15, 0.3, 0.5].map(val => (
                    <button
                        key={val}
                        id={`cash-percent-${(val * 100).toFixed(0)}`}
                        type="button"
                        onClick={handleCashPercentClick(val)}
                        style={{
                            marginRight: 8,
                            padding: "2px 10px",
                            background: cashPercent === val ? "#1976d2" : "#f5f5f5",
                            color: cashPercent === val ? "#fff" : "#1976d2",
                            border: "1px solid #1976d2",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: 13
                        }}
                    >
                        {(val * 100).toFixed(0)}%
                    </button>
                ))}
            </div>

            {error && (
                <div style={{ color: "#e53935", marginBottom: 16 }}>
                    {error}
                </div>
            )}
            {loading ? (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                    <span className="loader"></span>
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="range-risk-table" style={{ minWidth: 800, width: "100%", marginBottom: 50 }}>
                        <thead>
                            <tr>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Symbol</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Last Price</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Beta</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Average Range ($)</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Qty</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Per of cash</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Est. Dollar Risk</th>
                                <th style={{ padding: "12px 24px", textAlign: "center" }}>Avg Daily Volume (10d)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: "center", padding: "16px" }}>
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                (() => {
                                    const accountVal = Number(parseCurrencyInput(accountBalance));
                                    return data.map((row) => {
                                        const periodData = row.periods && row.periods[period] ? row.periods[period] : {};
                                        // Get all available ranges for 7, 30, 90 day periods
                                        const range7 = row.periods && row.periods[7] && row.periods[7].average_range !== undefined ? Number(row.periods[7].average_range) : null;
                                        const range30 = row.periods && row.periods[30] && row.periods[30].average_range !== undefined ? Number(row.periods[30].average_range) : null;
                                        const range90 = row.periods && row.periods[90] && row.periods[90].average_range !== undefined ? Number(row.periods[90].average_range) : null;
                                        // Default for slider max
                                        const defaultAvgRange = periodData.average_range !== undefined && periodData.average_range !== "N/A"
                                            ? Number(periodData.average_range)
                                            : 1;
                                        let avgRange = sliderValues[row.symbol] !== undefined
                                            ? sliderValues[row.symbol]
                                            : (periodData.average_range !== undefined ? periodData.average_range : "N/A");
                                        const avgHigh = periodData.average_high && !isNaN(periodData.average_high) && periodData.average_high > 0
                                            ? periodData.average_high
                                            : null;
                                        const lastPrice = row.last_close !== undefined && row.last_close !== null && row.last_close !== "N/A"
                                            ? Number(row.last_close)
                                            : null;
                                        const isPenny = lastPrice !== null && lastPrice < 5;

                                        const maxQty = avgHigh
                                            ? Math.floor((accountVal * cashPercent) / avgHigh)
                                            : null;
                                        const perOfCashPct = accountVal > 0
                                            ? ((accountVal * cashPercent) / accountVal) * 100
                                            : 0;

                                        // --- ADJUSTED ESTIMATED DOLLAR RISK LOGIC FOR PENNY STOCKS ---
                                        let estRiskLow = null;
                                        if (isPenny && maxQty !== null) {
                                            // Use the highest of 7, 30, 90 day ranges
                                            let maxRange = Math.max(
                                                range7 !== null ? range7 : -Infinity,
                                                range30 !== null ? range30 : -Infinity,
                                                range90 !== null ? range90 : -Infinity
                                            );
                                            // Cap range to 95% of last price if needed
                                            if (lastPrice !== null && maxRange > lastPrice) {
                                                maxRange = lastPrice * 0.95;
                                            }
                                            avgRange = maxRange; // <-- Show maxRange for penny stocks
                                            estRiskLow = maxRange > 0 ? maxRange * maxQty : null;
                                        } else {
                                            estRiskLow = (avgRange && avgHigh)
                                                ? avgRange * maxQty
                                                : null;
                                        }
                                        const avgVol = periodData.avg_daily_volume_10d;

                                        // Build metrics object for risk calculation
                                        const riskMetrics = {
                                            last_price: lastPrice !== null ? { value: lastPrice } : null,
                                            beta: row.beta !== undefined && row.beta !== null && !isNaN(row.beta)
                                                ? { value: Number(row.beta) }
                                                : null,
                                            average_range_percentage: avgRange !== undefined && avgRange !== "N/A" && lastPrice !== null
                                                ? { value: avgRange, lastPrice }
                                                : null,
                                            dollar_percentage_of_account_balance: { value: perOfCashPct },
                                            est_risk_dollar_percentage: estRiskLow !== null
                                                ? { value: estRiskLow, accountBalance: accountVal }
                                                : null,
                                            avg_daily_volume: avgVol !== undefined && avgVol !== "N/A" && !isNaN(avgVol)
                                                ? { value: Number(avgVol) }
                                                : null
                                        };

                                        const lastPriceUnderline = SHOW_UNDERLINES && lastPrice !== null
                                            ? getUnderlineColor("last_price", lastPrice)
                                            : "transparent";
                                        const avgRangeUnderline = SHOW_UNDERLINES && avgRange !== undefined && lastPrice !== null
                                            ? getUnderlineColor("average_range_percentage", avgRange, lastPrice)
                                            : "transparent";

                                        return (
                                            <React.Fragment key={row.symbol}>
                                                <tr>
                                                    {/* Symbol with weighted risk underline and popover */}
                                                    <SymbolCell symbol={row.symbol} metrics={riskMetrics} isPenny={isPenny} screen={screen} />

                                                    {/* Last Price with colored underline */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center", position: "relative" }}>
                                                        <div>
                                                            {row.last_close !== undefined && row.last_close !== null && row.last_close !== "N/A"
                                                                ? formatLastPrice(row.last_close)
                                                                : "N/A"}
                                                        </div>
                                                        {SHOW_UNDERLINES && (
                                                            <div
                                                                style={{
                                                                    borderBottom: `4px solid ${lastPriceUnderline}`,
                                                                    width: "60%",
                                                                    margin: "0 auto"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                    {/* Beta */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center", position: "relative" }}
                                                        title={row.beta !== undefined && row.beta !== null && !isNaN(row.beta) ? `Beta: ${row.beta}` : "N/A"}>
                                                        <div>
                                                            {row.beta !== undefined && row.beta !== null && !isNaN(row.beta)
                                                                ? Number(row.beta).toFixed(2)
                                                                : "N/A"}
                                                        </div>
                                                        {SHOW_UNDERLINES && row.beta !== undefined && row.beta !== null && !isNaN(row.beta) && (
                                                            <div
                                                                style={{
                                                                    borderBottom: `4px solid ${getUnderlineColor("beta", Number(row.beta))}`,
                                                                    width: "60%",
                                                                    margin: "0 auto"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                    {/* Average Range ($) with risk-colored slider */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center", position: "relative" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                                                            <input
                                                                type="range"
                                                                min={0}
                                                                max={
                                                                    (periodData.average_range !== undefined && periodData.average_range !== "N/A")
                                                                        ? Number(periodData.average_range) * 3
                                                                        : 3
                                                                }
                                                                step={0.01}
                                                                value={avgRange !== undefined && avgRange !== "N/A" ? avgRange : 0}
                                                                onChange={e => handleSliderChange(row.symbol, e.target.value)}
                                                                style={{
                                                                    width: "90%",
                                                                    cursor: "pointer",
                                                                    accentColor: avgRangeUnderline !== "transparent" ? avgRangeUnderline : "#1976d2"
                                                                }}
                                                            />
                                                            <span style={{ fontWeight: 600, fontSize: 13, color: "white" }}>
                                                                {avgRange !== undefined && avgRange !== "N/A" ? Number(avgRange).toFixed(2) : "N/A"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    {/* Qty -50% cash */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center" }}>
                                                        {maxQty !== null
                                                            ? formatIntegerWithCommas(maxQty)
                                                            : "N/A"}
                                                    </td>
                                                    {/* Per of cash as percentage */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center", position: "relative" }}>
                                                        <span
                                                            className="per-cash-toggle"
                                                            style={{
                                                                textDecoration: "underline",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                fontWeight: 600,
                                                                color: "#1976d2"
                                                            }}
                                                        >
                                                            {perOfCashPct.toFixed(0) + "%"}
                                                        </span>
                                                        {SHOW_UNDERLINES && (
                                                            <div
                                                                style={{
                                                                    borderBottom: `4px solid ${getUnderlineColor("dollar_percentage_of_account_balance", perOfCashPct)}`,
                                                                    width: "60%",
                                                                    margin: "0 auto"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                    {/* Est. Dollar Risk */}
                                                    <td style={{ padding: "10px 24px", textAlign: "center" }}>
                                                        <div>
                                                            {estRiskLow !== null && estRiskLow !== undefined && !isNaN(estRiskLow)
                                                                ? formatRiskAbbreviated(estRiskLow)
                                                                : "N/A"}
                                                        </div>
                                                        {SHOW_UNDERLINES && estRiskLow !== null && estRiskLow !== undefined && !isNaN(estRiskLow) && (
                                                            <div
                                                                style={{
                                                                    borderBottom: `4px solid ${getUnderlineColor("est_risk_dollar_percentage", estRiskLow, accountVal)}`,
                                                                    width: "60%",
                                                                    margin: "0 auto"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                    {/* Avg Daily Volume */}
                                                    <td style={{ padding: "10px 24px, 12px", textAlign: "center", position: "relative" }}>
                                                        <div>
                                                            {avgVol !== undefined && avgVol !== "N/A"
                                                                ? formatVolume(avgVol)
                                                                : "N/A"}
                                                        </div>
                                                        {SHOW_UNDERLINES && avgVol !== undefined && avgVol !== "N/A" && !isNaN(avgVol) && (
                                                            <div
                                                                style={{
                                                                    borderBottom: `4px solid ${getUnderlineColor("avg_daily_volume", Number(avgVol))}`,
                                                                    width: "60%",
                                                                    margin: "0 auto"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                                {/* No expandable area, modal is used instead */}
                                            </React.Fragment>
                                        );
                                    });
                                })()
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {/* Cash breakdown modal - render outside the table for proper centering */}
            {modalSymbol && (() => {
                const row = data.find(r => r.symbol === modalSymbol);
                if (!row) return null;
                const periodData = row.periods && row.periods[period] ? row.periods[period] : {};
                const avgRange = sliderValues[row.symbol] !== undefined
                    ? sliderValues[row.symbol]
                    : (periodData.average_range !== undefined ? periodData.average_range : "N/A");
                const avgHigh = periodData.average_high && !isNaN(periodData.average_high) && periodData.average_high > 0
                    ? periodData.average_high
                    : null;
                const accountVal = Number(parseCurrencyInput(accountBalance));
                return (
                    <CashBreakdownModal
                        open={!!modalSymbol}
                        onClose={() => setModalSymbol(null)}
                        symbol={modalSymbol}
                        cashPercentages={cashPercentages}
                        avgHigh={avgHigh}
                        avgRange={avgRange}
                        accountVal={accountVal}
                        getUnderlineColor={getUnderlineColor}
                        formatIntegerWithCommas={formatIntegerWithCommas}
                        formatRiskAbbreviated={formatRiskAbbreviated}
                    />
                );
            })()}

            {/* Glossary Section - Collapsed by default */}
            <div
                style={{
                    marginTop: 40,
                    padding: 24,
                    background: "transparent",
                    borderRadius: 8,
                    maxWidth: 900,


                    overflowY: "auto",
                    marginLeft: "auto",
                    marginRight: "auto",
                    fontSize: 15,
                    color: "inherit"
                }}
            >
                <button
                    id="glossary-toggle"
                    onClick={(e) => {
                        setShowGlossary((prev) => !prev);
                        analyticsHandler({
                            event_type: "button_click",
                            module_name: MODULE_NAME,
                            subcomponent: "glossary-toggle",
                            screen
                        })(e);
                    }}
                    style={{
                        background: "none",
                        border: "none",
                        color: "#1976d2",
                        fontWeight: 700,
                        fontSize: 16,
                        cursor: "pointer",
                        marginBottom: 8,
                        textDecoration: "underline"
                    }}
                    aria-expanded={showGlossary}
                    aria-controls="glossary-content"
                >
                    {showGlossary ? "Hide Glossary of Terms ▲" : "Show Glossary of Terms ▼"}
                </button>
                {showGlossary && (
                    <div id="glossary-content">
                        <h3 style={{ fontWeight: 700, marginBottom: 12, color: "inherit" }}>Glossary of Terms</h3>
                        <ul style={{ lineHeight: 1.7, marginLeft: 0, paddingLeft: 20 }}>
                            <li>
                                <b>Last session price</b> – Price equity closed during last session
                            </li>
                            <li>
                                <b>Beta</b> – A measure of a stock's volatility compared to the overall market. A beta above 1 means the stock is more volatile than the market; below 1 means less volatile.
                            </li>
                            <li>
                                <b>Average Range</b> – Average price over a date range (high minus low closing price). Used to estimate potential price movement. For penny stocks (under $5),using the largest range from either the 7day,30day or 90day.
                            </li>
                            <li>
                                <b>Qty</b> – Largest number of shares you can purchase with current equity
                            </li>
                            <li>
                                <b>Est. Risk</b> – Estimated dollar amount at risk if equity moves the full amount of the average range. For stocks below $5 using the largest range from either the 7day,30day or 90day. This is conservative considering these stocks can jump 100% without news.
                            </li>
                            <li>
                                <b>*</b>Low Priced stock – Indicates a low-priced stock (last price under $5) using penny stock risk thresholds and weights
                            </li>
                            <li><b>Avg. Daily Volume </b> - Average daily volume matters because it reflects how liquid a stock is—how many shares are actually changing hands on a typical day. Higher liquidity means orders fill quickly and with less slippage, so you can enter and exit positions closer to the price you expect. When volume is low, the order book is thinner, spreads widen, and even small orders can move the price, making it harder (and more expensive) to get in or out of a trade efficiently.
    If you want, I can turn this into a tooltip‑style sentence for your UI.
                            </li>

                        </ul>


                    </div>
                )}
            </div>
        </div>
    );
}

export default RangeBasedRisk;